import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { trackResourceLead } from '../lib/analytics';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Calendar, Tag, ArrowLeft, Loader2, AlertCircle, Download, Mail, X, FileText } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import Seo from '../components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState('');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/blog/${slug}`),
      axios.get(`${API}/blog/${slug}/materials`).catch(() => ({ data: [] })),
    ])
      .then(([{ data: p }, { data: m }]) => {
        setPost(p);
        setMaterials(m || []);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const openDownload = (m) => {
    setSelectedMaterial(m);
    setEmail('');
    setName('');
    setModalError('');
  };

  const confirmDownload = async () => {
    setModalError('');
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      setModalError('Por favor introduce un email válido.');
      return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/materials/${selectedMaterial.id}/download`,
        { email: email.trim(), name: name.trim() || null },
        { responseType: 'blob' }
      );
      const blobUrl = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = selectedMaterial.original_filename || `${selectedMaterial.title}.pdf`;
      a.click();
      URL.revokeObjectURL(blobUrl);
      trackResourceLead(`material_${selectedMaterial.slug || selectedMaterial.title}`);
      setMaterials((prev) => prev.map((m) => m.id === selectedMaterial.id ? { ...m, downloads: (m.downloads || 0) + 1 } : m));
      setSelectedMaterial(null);
    } catch (e) {
      setModalError('Hubo un error al descargar. Inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Seo path={`/blog/${slug}`} noindex />
      <Loader2 className="w-10 h-10 text-[#7DBDD9] animate-spin" />
    </div>
  );

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#E8F5FA] to-white flex items-center justify-center px-4">
        <Seo path={`/blog/${slug}`} title="Artículo no encontrado" noindex statusCode={404} />
        <div data-testid="blog-post-not-found" className="bg-white rounded-3xl p-8 max-w-md text-center shadow-xl">
          <AlertCircle className="w-14 h-14 text-[#F5A6A4] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#4A4A4A] mb-2">Artículo no encontrado</h1>
          <p className="text-gray-600 mb-6">Puede que haya sido movido o eliminado.</p>
          <button onClick={() => navigate('/blog')} data-testid="blog-not-found-back-btn" className="px-6 py-3 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white rounded-xl font-semibold">Volver al blog</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Seo
        path={`/blog/${post.slug}`}
        title={post.title}
        description={post.excerpt}
        image={post.cover_image || undefined}
        type="article"
      />
      <Navigation />
      <article className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-[#7DBDD9] mb-6" data-testid="back-to-blog-btn">
            <ArrowLeft className="w-4 h-4" /> Volver al blog
          </Link>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center flex-wrap gap-4 text-sm text-gray-500 mb-4">
              {post.published_at && (
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" />
                  {new Date(post.published_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
              )}
              {(post.tags || []).map((tag) => (
                <span key={tag} className="flex items-center gap-1 text-[#7DBDD9]">
                  <Tag className="w-3 h-3" />{tag}
                </span>
              ))}
            </div>

            <h1 className="text-3xl md:text-5xl font-bold text-[#4A4A4A] leading-tight mb-4" data-testid="blog-post-title">
              {post.title}
            </h1>
            {post.excerpt && <p className="text-lg text-gray-600 mb-8">{post.excerpt}</p>}

            {post.cover_image && (
              <div className="rounded-3xl overflow-hidden mb-10 shadow-lg">
                <img src={post.cover_image} alt={post.title} className="w-full h-auto" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              </div>
            )}

            <div className="prose prose-lg max-w-none prose-headings:text-[#4A4A4A] prose-a:text-[#7DBDD9] prose-strong:text-[#4A4A4A] prose-blockquote:border-[#F5A6A4] prose-blockquote:bg-[#FFF7F6]/50 prose-blockquote:px-4 prose-blockquote:py-2 prose-blockquote:rounded-r-lg" data-testid="blog-post-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
            </div>

            {/* Downloadable materials */}
            {materials.length > 0 && (
              <div className="mt-14 bg-gradient-to-br from-[#E8F5FA] to-[#FFF7F6] rounded-3xl p-8 border border-[#A8D8EA]/40" data-testid="blog-materials-section">
                <div className="flex items-center gap-3 mb-2">
                  <Download className="w-6 h-6 text-[#7DBDD9]" />
                  <h3 className="text-2xl font-bold text-[#4A4A4A]">Descarga gratis las plantillas</h3>
                </div>
                <p className="text-gray-600 mb-6">Deja tu email y te llegan los PDF imprimibles al instante.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {materials.map((m) => (
                    <div key={m.id} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex flex-col" data-testid={`blog-material-${m.id}`}>
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-11 h-11 rounded-xl bg-[#E8F5FA] flex items-center justify-center flex-shrink-0">
                          <FileText className="w-5 h-5 text-[#7DBDD9]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-[#4A4A4A] text-sm leading-tight mb-0.5">{m.title}</h4>
                          <p className="text-xs text-gray-500">{m.downloads || 0} descargas</p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 mb-4 line-clamp-2 flex-1">{m.description}</p>
                      <button
                        onClick={() => openDownload(m)}
                        data-testid={`blog-material-download-${m.id}`}
                        className="w-full py-2.5 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 shadow hover:shadow-md transition-all"
                      >
                        <Download className="w-4 h-4" /> Descargar PDF
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Reserve class CTA */}
            <div className="mt-10 bg-white rounded-3xl p-8 text-center border-2 border-[#F5A6A4]/40 shadow-sm">
              <p className="text-sm font-semibold text-[#F5A6A4] uppercase tracking-wider mb-2">¿Te ha gustado?</p>
              <h3 className="text-2xl font-bold text-[#4A4A4A] mb-3">¿Quieres aprender español conmigo?</h3>
              <p className="text-gray-600 mb-6">Reserva tu clase de prueba gratuita y empieza hoy mismo.</p>
              <Link to="/#booking" className="inline-block px-8 py-3.5 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white rounded-full font-bold shadow-lg hover:shadow-xl transition-all">
                Reservar clase gratuita
              </Link>
            </div>

            <div className="mt-12 pt-8 border-t border-gray-100 text-sm text-gray-500">
              Escrito por <span className="font-semibold text-[#4A4A4A]">{post.author}</span>
            </div>
          </motion.div>
        </div>
      </article>
      <Footer />

      {/* Download modal */}
      <AnimatePresence>
        {selectedMaterial && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => !submitting && setSelectedMaterial(null)}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
            data-testid="blog-download-modal"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl max-w-md w-full shadow-2xl overflow-hidden"
            >
              <div className="bg-gradient-to-r from-[#E8F5FA] to-[#FFF0EF] p-6 flex items-center justify-between">
                <h3 className="text-xl font-bold text-[#4A4A4A]">Descarga gratuita</h3>
                <button onClick={() => setSelectedMaterial(null)} className="w-9 h-9 rounded-full bg-white flex items-center justify-center hover:bg-gray-100" data-testid="blog-modal-close">
                  <X className="w-5 h-5 text-[#4A4A4A]" />
                </button>
              </div>
              <div className="p-6">
                <p className="text-gray-600 mb-1">Estás a punto de descargar:</p>
                <p className="font-bold text-[#4A4A4A] mb-5">{selectedMaterial.title}</p>
                <p className="text-sm text-gray-500 mb-4">Déjame tu email y te aviso cuando publique nuevos recursos gratis.</p>
                <div className="space-y-3">
                  <input
                    type="text" value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Tu nombre (opcional)"
                    data-testid="blog-download-name"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#A8D8EA] focus:ring-2 focus:ring-[#A8D8EA]/30 outline-none"
                  />
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@email.com"
                      data-testid="blog-download-email"
                      className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#A8D8EA] focus:ring-2 focus:ring-[#A8D8EA]/30 outline-none"
                    />
                  </div>
                </div>
                {modalError && <p className="text-red-500 text-sm mt-3">{modalError}</p>}
                <button
                  onClick={confirmDownload}
                  disabled={submitting}
                  data-testid="blog-download-confirm"
                  className="w-full mt-5 py-3.5 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {submitting ? <><Loader2 className="w-5 h-5 animate-spin" />Descargando...</> : <><Download className="w-5 h-5" />Descargar PDF</>}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
